import { BetaSettings } from '@/components/BetaSettings';
import { ClinicSwitcher } from '@/components/ClinicSwitcher';
import { Header, Sidebar } from '@/components/layout';
import { NoteDefaultsSettings } from '@/components/settings/NoteDefaultsSettings';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { hasBetaAccess, hasMemoryAccess, useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useSettings } from '@/hooks/useSettings';
import { useUserPreferencesForm } from '@/pages/settings/useUserPreferencesForm';
import { type Delegate, formatDelegatorLabel } from '@/types/delegate';
import {
  Building2,
  ChevronDown,
  ChevronUp,
  Clock,
  Loader2,
  Mail,
  MessageSquare,
  Mic,
  PenLine,
  Settings,
  Shield,
  Trash2,
  User,
  UserCheck,
  UserPlus,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'wouter';

export default function SettingsPage() {
  const { user, updateUser, isDelegateOnly, canSwitchOrg } = useAuth();
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // User settings
  const { toast } = useToast();
  const preferences = useUserPreferencesForm({
    enabled: !!user,
    onSaved: () => toast({ title: 'Stillingar vistaðar', description: 'Stillingar þínar hafa verið uppfærðar.' }),
    onSaveError: () => toast({ title: 'Villa', description: 'Mistókst að vista stillingar.', variant: 'destructive' }),
  });
  const { isLoading: isLoadingSettings, isSaving: isSavingSettings, setField } = preferences;
  const {
    preserveAppendSummary,
    enableTodoList,
    enableRuleBasedNormalization,
    defaultSessionMode,
    asrModelPreference,
    letterSignature,
  } = preferences.values;
  const setPreserveAppendSummary = (value: boolean) => setField('preserveAppendSummary', value);
  const setEnableTodoList = (value: boolean) => setField('enableTodoList', value);
  const setEnableRuleBasedNormalization = (value: boolean) => setField('enableRuleBasedNormalization', value);
  const setDefaultSessionMode = (value: 'conversation' | 'dictation') => setField('defaultSessionMode', value);
  const setAsrModelPreference = (value: string | null) => setField('asrModelPreference', value);
  const setLetterSignature = (value: string) => setField('letterSignature', value);

  // Beta-only streaming dictation preference — localStorage-backed via
  // useSettings so the dictation flow and this settings page see the same
  // value without their own copy.
  const { streamingDictation, setStreamingDictation } = useSettings();

  // Security settings
  const [allowAdminImpersonation, setAllowAdminImpersonation] = useState(true);
  const [isSavingImpersonation, setIsSavingImpersonation] = useState(false);
  const [isLoadingImpersonation, setIsLoadingImpersonation] = useState(true);

  // Delegate settings
  const [delegates, setDelegates] = useState<Delegate[]>([]);
  const [isLoadingDelegates, setIsLoadingDelegates] = useState(true);
  const [newDelegateSsn, setNewDelegateSsn] = useState('');
  const [isAddingDelegate, setIsAddingDelegate] = useState(false);
  const [removingDelegateId, setRemovingDelegateId] = useState<string | null>(null);
  const [delegateToRemove, setDelegateToRemove] = useState<Delegate | null>(null);

  // Load impersonation permission
  useEffect(() => {
    const fetchImpersonationPermission = async () => {
      try {
        const response = await fetch('/api/user/impersonation-permission', {
          credentials: 'include',
          headers: {
            Accept: 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setAllowAdminImpersonation(data.allow_admin_impersonation ?? true);
        }
      } catch (error) {
        console.error('Error fetching impersonation permission:', error);
      } finally {
        setIsLoadingImpersonation(false);
      }
    };

    if (user) {
      fetchImpersonationPermission();
    } else {
      setIsLoadingImpersonation(false);
    }
  }, [user]);

  // Load delegates
  useEffect(() => {
    const fetchDelegates = async () => {
      try {
        const response = await fetch('/api/delegates', {
          credentials: 'include',
          headers: {
            Accept: 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setDelegates(data.delegates || []);
        }
      } catch (error) {
        console.error('Error fetching delegates:', error);
      } finally {
        setIsLoadingDelegates(false);
      }
    };

    if (user) {
      fetchDelegates();
    } else {
      setIsLoadingDelegates(false);
    }
  }, [user]);

  const handleAddDelegate = async () => {
    if (!newDelegateSsn.trim()) {
      toast({
        title: 'Villa',
        description: 'Vinsamlegast sláðu inn kennitölu.',
        variant: 'destructive',
      });
      return;
    }

    // Format and validate SSN
    const cleanedSsn = newDelegateSsn.replace(/[^0-9]/g, '');
    if (cleanedSsn.length !== 10) {
      toast({
        title: 'Villa',
        description: 'Kennitala verður að vera 10 tölustafir.',
        variant: 'destructive',
      });
      return;
    }

    setIsAddingDelegate(true);

    try {
      const response = await fetch('/api/delegates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ delegate_ssn: cleanedSsn }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'Fulltrúi bætt við',
          description:
            data.delegate.status === 'pending'
              ? 'Fulltrúi hefur verið bætt við. Aðgangur virkjast þegar viðkomandi skráir sig inn.'
              : 'Fulltrúi hefur verið bætt við og hefur nú aðgang.',
        });
        setDelegates((prev) => [...prev, data.delegate]);
        setNewDelegateSsn('');
      } else {
        toast({
          title: 'Villa',
          description: data.error || 'Mistókst að bæta við fulltrúa.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error adding delegate:', error);
      toast({
        title: 'Villa',
        description: 'Óvænt villa kom upp. Vinsamlegast reyndu aftur.',
        variant: 'destructive',
      });
    } finally {
      setIsAddingDelegate(false);
    }
  };

  const handleRemoveDelegate = async (delegateId: string) => {
    setRemovingDelegateId(delegateId);

    try {
      const response = await fetch(`/api/delegates/${delegateId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast({
          title: 'Fulltrúi fjarlægður',
          description: 'Fulltrúi hefur verið fjarlægður og hefur ekki lengur aðgang.',
        });
        setDelegates((prev) => prev.filter((d) => d.id !== delegateId));
      } else {
        const data = await response.json();
        toast({
          title: 'Villa',
          description: data.error || 'Mistókst að fjarlægja fulltrúa.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error removing delegate:', error);
      toast({
        title: 'Villa',
        description: 'Óvænt villa kom upp. Vinsamlegast reyndu aftur.',
        variant: 'destructive',
      });
    } finally {
      setRemovingDelegateId(null);
    }
  };

  const getDelegateDisplayName = (delegate: Delegate) =>
    delegate.delegate_account_id ? formatDelegatorLabel(delegate.delegate_account_id) : 'Bíður innskráningar';

  const handleSaveEmail = async () => {
    // Validation
    if (!newEmail || !confirmEmail) {
      toast({
        title: 'Villa',
        description: 'Vinsamlegast fylltu út bæði netfangareitina.',
        variant: 'destructive',
      });
      return;
    }

    if (newEmail !== confirmEmail) {
      toast({
        title: 'Villa',
        description: 'Netföngin stemma ekki. Vinsamlegast reyndu aftur.',
        variant: 'destructive',
      });
      return;
    }

    if (!newEmail.includes('@') || !newEmail.split('@')[1].includes('.')) {
      toast({
        title: 'Villa',
        description: 'Ógilt snið á netfangi.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/user/email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ newEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'Netfang uppfært!',
          description: 'Netfangið þitt hefur verið uppfært.',
        });

        // Update user in context
        updateUser({ email: newEmail });

        // Close edit form and reset
        setIsEditingEmail(false);
        setNewEmail('');
        setConfirmEmail('');
      } else {
        toast({
          title: 'Villa',
          description: data.error || 'Mistókst að uppfæra netfang.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error updating email:', error);
      toast({
        title: 'Villa',
        description: 'Óvænt villa kom upp. Vinsamlegast reyndu aftur.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Patches only the fields the user edited. Default template ids are never sent
  // from here — they are owned by /api/templates/defaults.
  const handleSaveSettings = preferences.save;

  const handleSaveImpersonationPermission = async (allow: boolean) => {
    setIsSavingImpersonation(true);

    try {
      const response = await fetch('/api/user/impersonation-permission', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          allow_admin_impersonation: allow,
        }),
      });

      if (response.ok) {
        setAllowAdminImpersonation(allow);
        toast({
          title: allow ? 'Heimild virkjuð' : 'Heimild afvirkjuð',
          description: allow
            ? 'Kerfisstjóri getur nú stillt reikninginn þinn.'
            : 'Kerfisstjóri getur ekki lengur stillt reikninginn þinn.',
        });
      } else {
        const data = await response.json();
        toast({
          title: 'Villa',
          description: data.error || 'Mistókst að uppfæra stillingu.',
          variant: 'destructive',
        });
        // Revert checkbox
        setAllowAdminImpersonation(!allow);
      }
    } catch (error) {
      console.error('Error saving impersonation permission:', error);
      toast({
        title: 'Villa',
        description: 'Óvænt villa kom upp. Vinsamlegast reyndu aftur.',
        variant: 'destructive',
      });
      // Revert checkbox
      setAllowAdminImpersonation(!allow);
    } finally {
      setIsSavingImpersonation(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header title="Stillingar" />

        <main className="flex-1 overflow-hidden bg-slate-50/30">
          <ScrollArea className="h-full">
            <div className="mx-auto max-w-4xl p-8">
              <div className="mb-8">
                <h2 className="font-bold text-2xl text-slate-900">Stillingar</h2>
              </div>

              <div className="space-y-6">
                {/* Account Settings */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2 text-slate-900">
                      <User className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">Aðgangur</CardTitle>
                    </div>
                    <CardDescription>Umsjón með þínu netfangi og staðfestingu.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <Label>Netfang</Label>
                      <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
                        {/* Email Display */}
                        <div className="flex items-center justify-between p-4">
                          <div className="flex items-center gap-3">
                            <Mail className="h-5 w-5 text-slate-400" />
                            <div>
                              <p className="font-semibold text-slate-900 text-sm">
                                {user?.email || 'Ekkert netfang skráð'}
                              </p>
                              <p className="text-slate-500 text-xs">Notað fyrir tilkynningar og aðgangsendurheimtu</p>
                            </div>
                          </div>

                          <Button variant="outline" size="sm" onClick={() => setIsEditingEmail(!isEditingEmail)}>
                            {isEditingEmail ? (
                              <>
                                <ChevronUp className="mr-1 h-4 w-4" />
                                Loka
                              </>
                            ) : (
                              <>
                                <ChevronDown className="mr-1 h-4 w-4" />
                                Breyta netfangi
                              </>
                            )}
                          </Button>
                        </div>

                        {/* Edit Form - Slide Down */}
                        <div
                          className={`grid transition-all duration-300 ease-in-out ${isEditingEmail ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}
                          `}
                        >
                          <div className="overflow-hidden">
                            <div className="space-y-4 border-t bg-slate-50/50 p-4">
                              <div className="space-y-2">
                                <Label htmlFor="new-email" className="font-medium text-sm">
                                  Nýtt netfang
                                </Label>
                                <Input
                                  id="new-email"
                                  type="email"
                                  placeholder="netfang@example.is"
                                  value={newEmail}
                                  onChange={(e) => setNewEmail(e.target.value)}
                                  disabled={isSubmitting}
                                  className="bg-white"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="confirm-email" className="font-medium text-sm">
                                  Staðfesta nýtt netfang
                                </Label>
                                <Input
                                  id="confirm-email"
                                  type="email"
                                  placeholder="netfang@example.is"
                                  value={confirmEmail}
                                  onChange={(e) => setConfirmEmail(e.target.value)}
                                  disabled={isSubmitting}
                                  className="bg-white"
                                />
                              </div>
                              <div className="flex justify-end gap-2 pt-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setIsEditingEmail(false);
                                    setNewEmail('');
                                    setConfirmEmail('');
                                  }}
                                  disabled={isSubmitting}
                                >
                                  Hætta við
                                </Button>
                                <Button size="sm" onClick={handleSaveEmail} disabled={isSubmitting}>
                                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                  Vista breytingar
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Organization Selection - routed through auth org switching */}
                {canSwitchOrg && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2 text-slate-900">
                        <Building2 className="h-5 w-5 text-primary" />
                        <CardTitle className="text-lg">Stöð</CardTitle>
                      </div>
                      <CardDescription>Veldu stöð til að vinna á.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ClinicSwitcher />
                    </CardContent>
                  </Card>
                )}

                {/* Session Preferences */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2 text-slate-900">
                      <Mic className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">Lotur</CardTitle>
                    </div>
                    <CardDescription>Stillingar fyrir upptökulotur.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {isLoadingSettings ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="space-y-3">
                          <Label className="font-medium text-sm">Sjálfgefin tegund lotu</Label>
                          <RadioGroup
                            value={defaultSessionMode}
                            onValueChange={(value) => setDefaultSessionMode(value as 'conversation' | 'dictation')}
                            className="flex flex-col gap-3"
                          >
                            <div className="flex cursor-pointer items-center space-x-3 rounded-lg border bg-white p-3 transition-colors hover:bg-slate-50">
                              <RadioGroupItem value="conversation" id="mode-conversation" />
                              <label
                                htmlFor="mode-conversation"
                                className="flex flex-1 cursor-pointer items-center gap-3"
                              >
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                                  <MessageSquare className="h-4 w-4 text-primary" />
                                </div>
                                <div>
                                  <p className="font-medium text-sm">Viðtal</p>
                                  <p className="text-muted-foreground text-xs">
                                    Samtal við sjúkling með greiningu á ræðumönnum
                                  </p>
                                </div>
                              </label>
                            </div>
                            <div className="flex cursor-pointer items-center space-x-3 rounded-lg border bg-white p-3 transition-colors hover:bg-slate-50">
                              <RadioGroupItem value="dictation" id="mode-dictation" />
                              <label htmlFor="mode-dictation" className="flex flex-1 cursor-pointer items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                                  <Mic className="h-4 w-4 text-primary" />
                                </div>
                                <div>
                                  <p className="font-medium text-sm">Diktering</p>
                                  <p className="text-muted-foreground text-xs">Einræða læknis með frjálsri umritun</p>
                                </div>
                              </label>
                            </div>
                          </RadioGroup>
                        </div>

                        <Separator className="my-4" />

                        <div className="space-y-3">
                          <Label className="font-medium text-sm">Stillingar fyrir dikteringu</Label>
                          <div className="flex items-start space-x-3 rounded-lg border bg-white p-3">
                            <Checkbox
                              id="enable-rule-based-normalization"
                              checked={enableRuleBasedNormalization}
                              onCheckedChange={(checked) => setEnableRuleBasedNormalization(checked as boolean)}
                              disabled={isSavingSettings}
                            />
                            <div className="grid gap-1.5 leading-none">
                              <label
                                htmlFor="enable-rule-based-normalization"
                                className="cursor-pointer font-medium text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                Virkja umritun greinarmerkja
                              </label>
                              <p className="text-muted-foreground text-sm">
                                Umbreytir töldum greinarmerkjum í tákn (t.d. &quot;punktur&quot; → &quot;.&quot;,
                                &quot;ný lína&quot; → línubil).
                              </p>
                            </div>
                          </div>
                        </div>

                        <Button
                          onClick={handleSaveSettings}
                          disabled={isSavingSettings}
                          className="mt-4 w-full sm:w-auto"
                        >
                          {isSavingSettings && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Vista stillingar
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* User Preferences */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2 text-slate-900">
                      <Settings className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">Stillingar fyrir samantekt og verkefnalista</CardTitle>
                    </div>
                    <CardDescription>Sérsníðu hvernig samantektir og verkefnalistar virka.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {isLoadingSettings ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <>
                        <div className="space-y-4">
                          <div className="flex items-start space-x-3">
                            <Checkbox
                              id="preserve-append"
                              checked={preserveAppendSummary}
                              onCheckedChange={(checked) => setPreserveAppendSummary(checked as boolean)}
                              disabled={isSavingSettings}
                            />
                            <div className="grid gap-1.5 leading-none">
                              <label
                                htmlFor="preserve-append"
                                className="cursor-pointer font-medium text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                Halda fyrri samantekt
                              </label>
                              <p className="text-muted-foreground text-sm">
                                Þegar haldið er áfram með fyrra samtal, haltu fyrri samantekt óbreyttri og bættu aðeins
                                við nýjum upplýsingum.
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start space-x-3">
                            <Checkbox
                              id="enable-todo"
                              checked={enableTodoList}
                              onCheckedChange={(checked) => setEnableTodoList(checked as boolean)}
                              disabled={isSavingSettings}
                            />
                            <div className="grid gap-1.5 leading-none">
                              <label
                                htmlFor="enable-todo"
                                className="cursor-pointer font-medium text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                Virkja verkefnalista
                              </label>
                              <p className="text-muted-foreground text-sm">
                                Virkja verkefnalista sem birtist eftir að upptöku lýkur.
                              </p>
                            </div>
                          </div>
                        </div>

                        <Button onClick={handleSaveSettings} disabled={isSavingSettings} className="w-full sm:w-auto">
                          {isSavingSettings && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Vista stillingar
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>

                <NoteDefaultsSettings />

                {/* Letter Signature */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2 text-slate-900">
                      <PenLine className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">Undirskrift í bréfum</CardTitle>
                    </div>
                    <CardDescription>
                      Sérsníðu undirskrift sem birtist í samantektarbréfum til skjólstæðinga.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {isLoadingSettings ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="letter-signature">Undirskrift</Label>
                          <Textarea
                            id="letter-signature"
                            value={letterSignature}
                            onChange={(e) => setLetterSignature(e.target.value)}
                            placeholder={`Bestu kveðjur,\n${user?.name || 'Nafn læknisins'}\nSérnámslæknir í heimilislækningum`}
                            rows={4}
                            disabled={isSavingSettings}
                            className="resize-none"
                          />
                          <p className="text-muted-foreground text-sm">
                            Þessi undirskrift birtist neðst í samantektarbréfum. Ef tómt er notað sjálfgefið:
                            &quot;Bestu kveðjur, [nafn]&quot;.
                          </p>
                        </div>
                        <Button onClick={handleSaveSettings} disabled={isSavingSettings} className="w-full sm:w-auto">
                          {isSavingSettings && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Vista stillingar
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Security Settings */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2 text-slate-900">
                      <Shield className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">Öryggi og persónuvernd</CardTitle>
                    </div>
                    <CardDescription>Stillingar sem varða aðgang að reikningnum þínum.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {isLoadingImpersonation ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-start space-x-3">
                          <Checkbox
                            id="allow-impersonation"
                            checked={allowAdminImpersonation}
                            onCheckedChange={(checked) => handleSaveImpersonationPermission(checked as boolean)}
                            disabled={isSavingImpersonation}
                          />
                          <div className="grid gap-1.5 leading-none">
                            <label
                              htmlFor="allow-impersonation"
                              className="cursor-pointer font-medium text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Leyfa kerfisstjóra að skrá sig inn á þinn aðgang
                            </label>
                            <p className="text-muted-foreground text-sm">
                              Þegar þetta er virkt getur kerfisstjóri skráð sig inn á þinn aðgang til að stilla sniðmát
                              og aðrar stillingar á reikningnum þínum.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Delegate Access Settings — only for users who have their own clinic (not delegate-only) */}
                {!isDelegateOnly && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2 text-slate-900">
                        <Users className="h-5 w-5 text-primary" />
                        <CardTitle className="text-lg">Fulltrúaaðgangur</CardTitle>
                      </div>
                      <CardDescription>
                        Stjórna hverjir hafa aðgang að mínum upptökum. Fulltrúar geta skoðað og breytt samantektum en
                        geta ekki tekið upp nýjar lotur.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {isLoadingDelegates ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : (
                        <>
                          {/* Add delegate form */}
                          <div className="space-y-3">
                            <Label htmlFor="delegate-ssn" className="font-medium text-sm">
                              Bæta við fulltrúa
                            </Label>
                            <div className="flex gap-2">
                              <Input
                                id="delegate-ssn"
                                type="text"
                                placeholder="Kennitala (10 tölustafir)"
                                value={newDelegateSsn}
                                onChange={(e) => setNewDelegateSsn(e.target.value)}
                                disabled={isAddingDelegate}
                                className="flex-1"
                                maxLength={11}
                              />
                              <Button onClick={handleAddDelegate} disabled={isAddingDelegate || !newDelegateSsn.trim()}>
                                {isAddingDelegate ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <UserPlus className="mr-2 h-4 w-4" />
                                    Bæta við
                                  </>
                                )}
                              </Button>
                            </div>
                            <p className="text-muted-foreground text-xs">
                              Sláðu inn kennitölu þess sem á að fá fulltrúaaðgang. Ef viðkomandi er ekki þegar með
                              aðgang verður boðið virkjað þegar hann skráir sig inn.
                            </p>
                          </div>

                          {/* Delegate list */}
                          {delegates.length > 0 && (
                            <div className="space-y-3">
                              <Label className="font-medium text-sm">Fulltrúar</Label>
                              <div className="space-y-2">
                                {delegates.map((delegate) => (
                                  <div
                                    key={delegate.id}
                                    className="flex items-center justify-between rounded-lg border bg-white p-3"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div
                                        className={`flex h-8 w-8 items-center justify-center rounded-full ${
                                          delegate.status === 'active' ? 'bg-green-100' : 'bg-amber-100'
                                        }`}
                                      >
                                        {delegate.status === 'active' ? (
                                          <UserCheck className="h-4 w-4 text-green-600" />
                                        ) : (
                                          <Clock className="h-4 w-4 text-amber-600" />
                                        )}
                                      </div>
                                      <div>
                                        <p className="font-medium text-sm">{getDelegateDisplayName(delegate)}</p>
                                        <div className="flex items-center gap-2">
                                          <Badge
                                            variant={delegate.status === 'active' ? 'default' : 'secondary'}
                                            className={`text-xs ${
                                              delegate.status === 'active'
                                                ? 'bg-green-100 text-green-700 hover:bg-green-100'
                                                : 'bg-amber-100 text-amber-700 hover:bg-amber-100'
                                            }`}
                                          >
                                            {delegate.status === 'active' ? 'Virkur' : 'Bíður'}
                                          </Badge>
                                          {delegate.permissions.length > 0 && (
                                            <span className="text-muted-foreground text-xs">
                                              {delegate.permissions.join(', ')}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setDelegateToRemove(delegate)}
                                      disabled={removingDelegateId === delegate.id}
                                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                    >
                                      {removingDelegateId === delegate.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Trash2 className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {delegates.length === 0 && (
                            <div className="py-6 text-center text-muted-foreground text-sm">
                              Engir fulltrúar skráðir. Bættu við fulltrúa til að leyfa öðrum aðgang að þínum upptökum.
                            </div>
                          )}

                          <AlertDialog
                            open={!!delegateToRemove}
                            onOpenChange={(open) => !open && setDelegateToRemove(null)}
                          >
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Fjarlægja fulltrúa?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Ertu viss um að þú viljir fjarlægja{' '}
                                  <strong>{delegateToRemove && getDelegateDisplayName(delegateToRemove)}</strong> sem
                                  fulltrúa? Viðkomandi mun ekki lengur hafa aðgang að þínum upptökum.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Hætta við</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-red-600 hover:bg-red-700"
                                  onClick={async () => {
                                    if (delegateToRemove) {
                                      await handleRemoveDelegate(delegateToRemove.id);
                                      setDelegateToRemove(null);
                                    }
                                  }}
                                >
                                  Fjarlægja
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Beta Settings - only visible to beta users (features:beta permission) */}
                {hasBetaAccess(user) && (
                  <BetaSettings
                    asrModelPreference={asrModelPreference}
                    setAsrModelPreference={setAsrModelPreference}
                    streamingDictation={streamingDictation}
                    setStreamingDictation={setStreamingDictation}
                    onSave={handleSaveSettings}
                    isSaving={isSavingSettings}
                  />
                )}

                {/* Minni (Scribe User Memory) - entry point to /settings/memory; hidden when the memory routes would 403 */}
                {hasMemoryAccess(user) && (
                  <Link href="/settings/memory">
                    <Card className="cursor-pointer transition-colors hover:bg-slate-50">
                      <CardHeader>
                        <CardTitle className="text-lg">Minni</CardTitle>
                        <CardDescription>
                          Skríban lærir af breytingum þínum á samantektum og býr til leiðbeiningar um stíl, uppbyggingu,
                          orðalag og fagorð fyrir næstu samantektir.
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  </Link>
                )}
              </div>
            </div>
          </ScrollArea>
        </main>
      </div>
    </div>
  );
}
